/**
 * RequireClinicalMfa - blocks patient chart and encounter routes until app MFA is enabled.
 * Entra-only SSO users satisfy MFA via Conditional Access and are not blocked.
 */
import { useContext } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { AuthContext, isClinicalRole } from "@/providers/auth-context";
import type { User } from "@/types";

function needsAppMfaSetup(user: User): boolean {
  if (!isClinicalRole(user.role)) return false;
  if (user.mfa_enabled) return false;
  // If the org/admin hasn't enforced MFA for this user, don't force setup
  if (!user.mfa_enforced) return false;
  // Entra SSO + Conditional Access satisfies org MFA at identity layer
  if (user.auth_provider === "entra") return false;
  return true;
}

export function RequireClinicalMfa() {
  const auth = useContext(AuthContext);

  if (auth?.isLoading) return null;

  if (auth?.user && needsAppMfaSetup(auth.user)) {
    return <Navigate to="/account/security?setup_mfa=1" replace />;
  }

  return <Outlet />;
}
